window.ShopSanteInventory = window.ShopSanteInventory || (() => {
  const requests = new Map();
  const policyRequests = new Map();
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const nodes = connection => connection?.nodes || [];
  const onlineInventoryOnly = id => [...document.querySelectorAll('[data-online-inventory-only="true"][data-variant-id]')]
    .some(node => String(node.dataset.variantId) === String(id).split('/').pop());
  const variantFields = `          id title availableForSale quantityAvailable requiresComponents requiresShipping
          product { id handle isGiftCard tags productType }
          components(first: 100) {
            pageInfo { hasNextPage }
            nodes {
              quantity
              productVariant {
                id title availableForSale quantityAvailable requiresShipping product { id handle tags productType }
                storeAvailability(first: 250) {
                  pageInfo { hasNextPage }
                  nodes { available quantityAvailable location { id name } }
                }
              }
            }
          }
          storeAvailability(first: 250) {
            pageInfo { hasNextPage }
            nodes { available quantityAvailable location { id name } }
          }`;
  // Existing cart lines only need their component usage and selling location.
  // Stock and selling policy are checked on the variants being added below.
  const cartVariantFields = `id requiresComponents requiresShipping
    product { tags productType }
    components(first: 100) {
      pageInfo { hasNextPage }
      nodes { quantity productVariant { id requiresShipping product { tags productType } } }
    }`;
  const query = `query ProductInventory($id: ID!) { product(id: $id) { id title availableForSale variants(first: 250) { pageInfo { hasNextPage } nodes { ${variantFields} } } } }`;
  function storeName(product) {
    const handle = localStorage.getItem('selected-store');
    if (!handle) return 'Shop Santé Inc.';
    const selected = [...document.querySelectorAll('.store-selector-item[data-handle]')]
      .find(item => item.dataset.handle === handle);
    if (selected?.dataset.title) return selected.dataset.title;
    const variants = nodes(product?.variants);
    const allVariants = variants.concat(variants.flatMap(variant => nodes(variant.components).map(component => component.productVariant)));
    return allVariants.flatMap(variant => nodes(variant?.storeAvailability))
      .find(item => normalize(item.location?.name) === handle)?.location?.name || '';
  }
  function units(variant, location) {
    // Overselling is an online purchase rule, never a promise of local pickup stock.
    if (location === 'Shop Santé Inc.' && variant?.availableForSale === true
      && (variant.inventoryPolicy === 'continue' || variant.inventoryTracked === false)) return Infinity;
    if (location === 'Shop Santé Inc.' && variant?.product?.isGiftCard === true
      && variant.requiresShipping === false && variant.availableForSale === true
      && !variant.requiresComponents && !nodes(variant.components).length) return Infinity;
    if (location === 'Shop Sante Wholesale') {
      const wholesaleQuantity = Number(variant?.quantityAvailable);
      if (variant?.availableForSale !== false && Number.isFinite(wholesaleQuantity)) {
        return Math.max(0, Math.floor(wholesaleQuantity));
      }
    }
    if (!variant || !location || variant.storeAvailability?.pageInfo?.hasNextPage) return 0;
    const row = nodes(variant.storeAvailability).find(item => item.location?.name === location);
    const value = Number(row?.quantityAvailable);
    return row?.available && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  }
  function requirements(variant) {
    const components = nodes(variant?.components);
    if (variant?.components?.pageInfo?.hasNextPage || (variant?.requiresComponents && !components.length)) return null;
    const rows = components.length ? components : [{ quantity: 1, productVariant: variant }];
    const grouped = new Map();
    for (const row of rows) {
      const fee = row.productVariant?.requiresShipping === false
        && row.productVariant?.product?.tags?.some(tag => String(tag).trim().toLowerCase() === 'feebee fee');
      if (fee) continue;
      const count = Number(row.quantity);
      if (!row.productVariant?.id || !Number.isInteger(count) || count <= 0) return null;
      const existing = grouped.get(row.productVariant.id);
      if (existing) existing.quantity += count;
      else grouped.set(row.productVariant.id, { variant: row.productVariant, quantity: count });
    }
    return [...grouped.values()];
  }
  function state(variant, location) {
    const required = requirements(variant);
    const isBundle = Boolean(variant?.requiresComponents || nodes(variant?.components).length);
    const quantity = required?.length && variant?.availableForSale !== false
      ? Math.min(...required.map(item => Math.floor(units(item.variant, location) / item.quantity))) : 0;
    return { quantity, available: quantity > 0, isBundle, unlimited: quantity === Infinity };
  }
  function inventoryLocation(variant, selectedLocation) {
    const productType = String(variant?.product?.productType || '').trim().toLowerCase();
    const tags = variant?.product?.tags || [];
    const wholesale = productType === 'wholesale'
      || tags.some(tag => String(tag).trim().toLowerCase() === 'wholesale');
    return wholesale ? 'Shop Sante Wholesale' : selectedLocation;
  }
  async function hydratePolicies(variants, fresh = false) {
    const all = variants.filter(Boolean).flatMap(variant => [variant, ...nodes(variant.components).map(row => row.productVariant)]).filter(Boolean);
    const isFee = variant => variant.requiresShipping === false && variant.product?.tags?.some(tag => String(tag).trim().toLowerCase() === 'feebee fee');
    const products = [...new Map(all.filter(variant => !isFee(variant) && variant.product?.handle).map(variant => [variant.product.id, variant.product])).values()];
    // A small worker pool avoids a request burst on large carts or recommendations.
    let cursor = 0;
    const policies = new Map();
    await Promise.all(Array.from({ length: Math.min(4, products.length) }, async () => {
      while (cursor < products.length) {
        const product = products[cursor++];
        const key = `${window.Shopify?.routes?.root || '/'}:${window.Shopify?.theme?.id || ''}:${product.id}`;
        const cached = policyRequests.get(key);
        let promise = !fresh && cached && Date.now() - cached.time < 15000 ? cached.promise : null;
        if (!promise) {
          promise = (async () => {
            let url = new URL(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`, window.location.origin);
            const result = new Map();
            const visited = new Set();
            while (url) {
              url.searchParams.set('section_id', 'helper-inventory-policy');
              if (window.Shopify?.theme?.role !== 'main' && window.Shopify?.theme?.id) url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
              if (url.origin !== window.location.origin || visited.has(url.href)) throw unavailableError();
              visited.add(url.href);
              const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
              if (!response.ok) throw unavailableError();
              const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
              const node = doc.querySelector('[data-shop-sante-inventory-policy]');
              const data = node && JSON.parse(node.textContent);
              if (!data || String(data.productId) !== String(product.id).split('/').pop() || !Array.isArray(data.variants)) throw unavailableError();
              for (const policy of data.variants) {
                if (!['continue', 'deny'].includes(policy.policy) || typeof policy.tracked !== 'boolean') throw unavailableError();
                result.set(String(policy.id), policy);
              }
              url = data.next ? new URL(data.next, url) : null;
            }
            return result;
          })().catch(error => { policyRequests.delete(key); throw error; });
          policyRequests.set(key, { time: Date.now(), promise });
        }
        policies.set(product.id, await promise);
      }
    }));
    for (const variant of all) {
      // Fee-only lines are intentionally excluded by requirements().
      const fee = variant.requiresShipping === false && variant.product?.tags?.some(tag => String(tag).trim().toLowerCase() === 'feebee fee');
      if (fee) continue;
      const policy = policies.get(variant.product?.id)?.get(String(variant.id).split('/').pop());
      if (!policy) throw unavailableError();
      variant.inventoryPolicy = policy.policy;
      variant.inventoryTracked = policy.tracked;
    }
    return variants;
  }
  async function load({ productId, domain = window.KROWN?.settings?.store_domain, token = window.KROWN?.settings?.storefront_api_key, fresh = false }) {
    if (!productId || !domain || !token) throw new Error('Inventory configuration unavailable');
    const gid = String(productId).startsWith('gid://') ? String(productId) : `gid://shopify/Product/${productId}`;
    const key = domain + gid;
    const cached = requests.get(key);
    if (!fresh && cached && Date.now() - cached.time < 15000) return cached.promise;
    const promise = fetch(`https://${domain}/api/2026-07/graphql.json`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query, variables: { id: gid } })
    }).then(async response => {
      const data = await response.json();
      if (!response.ok || data.errors?.length || !data.data?.product || data.data.product.variants?.pageInfo?.hasNextPage) throw new Error('Inventory unavailable');
      await hydratePolicies(nodes(data.data.product.variants), fresh);
      return data;
    }).catch(error => { requests.delete(key); throw error; });
    requests.set(key, { promise, time: Date.now() });
    return promise;
  }
  const userError = message => Object.assign(new Error(message), { userMessage: true });
  const stockError = () => userError(document.documentElement.lang?.startsWith('fr')
    ? 'Cette quantité n’est plus disponible pour la boutique sélectionnée. Vérifiez votre panier ou choisissez une autre boutique.'
    : 'This quantity is no longer available at the selected store. Check your cart or choose another store.');
  const unavailableError = () => userError(document.documentElement.lang?.startsWith('fr')
    ? 'Impossible de vérifier le stock. Réessayez dans un instant.'
    : 'Unable to check stock. Please try again shortly.');
  async function readCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw unavailableError();
      const cart = await response.json();
      if (!Array.isArray(cart?.items)) throw unavailableError();
      return cart;
    } catch (_) {
      throw unavailableError();
    }
  }
  function checkDemand(items, cartItems, variants, location) {
    const demand = new Map();
    const wanted = new Map();
    const byId = new Map(variants.filter(Boolean).map(variant => [String(variant.id).split('/').pop(), variant]));
    for (const item of items) {
      const variant = byId.get(String(item.id));
      const required = requirements(variant);
      const itemLocation = inventoryLocation(variant, location);
      if (!required || !state(variant, itemLocation).available || !Number.isInteger(item.quantity) || item.quantity < 1) throw stockError();
      for (const row of required) {
        const key = `${row.variant.id}\n${itemLocation}`;
        demand.set(key, (demand.get(key) || 0) + row.quantity * item.quantity);
        wanted.set(key, { variant: row.variant, location: itemLocation });
      }
    }
    for (const line of cartItems) {
      const variant = byId.get(String(line.variant_id || line.id));
      const required = requirements(variant) || [{ variant: { id: `gid://shopify/ProductVariant/${line.variant_id || line.id}` }, quantity: 1 }];
      const itemLocation = inventoryLocation(variant, location);
      for (const row of required) {
        const key = `${row.variant.id}\n${itemLocation}`;
        if (wanted.has(key)) demand.set(key, (demand.get(key) || 0) + row.quantity * Number(line.quantity || 0));
      }
    }
    for (const [key, quantity] of demand) {
      const row = wanted.get(key);
      if (quantity > units(row.variant, row.location)) throw stockError();
    }
    return true;
  }
  async function fetchCartVariants(ids, fields, batchSize, domain, token) {
    const variants = [];
    for (let offset = 0; offset < ids.length; offset += batchSize) {
      const batch = ids.slice(offset, offset + batchSize);
      let response;
      let result;
      try {
        response = await fetch(`https://${domain}/api/2026-07/graphql.json`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({
            query: `query CartInventory($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { ${fields} } } }`,
            variables: { ids: batch.map(id => `gid://shopify/ProductVariant/${id}`) }
          })
        });
        result = await response.json();
      } catch (_) {
        throw unavailableError();
      }
      if (!response.ok || result.errors?.length || !Array.isArray(result.data?.nodes)
        || result.data.nodes.length !== batch.length) throw unavailableError();
      variants.push(...result.data.nodes);
    }
    return variants;
  }
  async function validateAddition(items, existingCart) {
    try {
      if (items.length && items.every(item => onlineInventoryOnly(item.id))) return true;
      const selected = localStorage.getItem('selected-store');
      const cart = existingCart || await readCart();
      const { store_domain: domain, storefront_api_key: token } = window.KROWN?.settings || {};
      if (!domain || !token || !Array.isArray(cart.items)) throw unavailableError();
      const addedIds = [...new Set(items.map(item => String(item.id).split('/').pop()))];
      const addedIdSet = new Set(addedIds);
      const cartIds = [...new Set(cart.items.map(item => String(item.variant_id || item.id)))]
        .filter(id => !addedIdSet.has(id));
      // Keep complete stock data for requested variants. Existing lines only
      // contribute quantity and component demand, even in a large cart.
      const added = await fetchCartVariants(addedIds, variantFields, 5, domain, token);
      const existing = await fetchCartVariants(cartIds, cartVariantFields, 20, domain, token);
      await hydratePolicies(added.filter(Boolean), true);
      if (selected !== localStorage.getItem('selected-store')) throw stockError();
      const variants = added.concat(existing);
      return checkDemand(items, cart.items, variants, storeName({ variants: { nodes: variants.filter(Boolean) } }));
    } catch (error) {
      if (error?.userMessage) throw error;
      throw unavailableError();
    }
  }
  async function validateChange(key, quantity) {
    const cart = await readCart();
    const item = cart.items.find(item => item.key === key || String(item.id) === String(key));
    if (!item) throw unavailableError();
    if (quantity <= item.quantity) return;
    if (onlineInventoryOnly(item.variant_id || item.id)) return true;
    return validateAddition([{ id: item.variant_id || item.id, quantity: quantity - item.quantity }], cart);
  }
  let mutationQueue = Promise.resolve();
  async function lock() {
    let release;
    const next = new Promise(resolve => { release = resolve; });
    const previous = mutationQueue;
    mutationQueue = next;
    await previous;
    return release;
  }
  return { load, state, storeName, requirements, units, checkDemand, validateAddition, validateChange, lock };

})();

window.ShopSanteBundleSubscriptions = (() => {
  const cache = new Map();
  const french = () => document.documentElement.lang.toLowerCase().startsWith('fr');
  const message = code => ({
    sale: french() ? 'Abonnement indisponible : ce format ou un produit lié est en promotion.' : 'Subscription unavailable: this format or a linked product is on sale.',
    plan: french() ? 'Abonnement indisponible pour les produits liés à ce format.' : 'Subscription unavailable for the products linked to this format.',
    data: french() ? 'Impossible de vérifier cet abonnement. Réessayez dans un instant.' : 'Unable to check this subscription. Please try again shortly.'
  })[code];
  const query = `query BundleSubscription($id: ID!) { node(id: $id) { ... on ProductVariant {
    id requiresComponents price { amount currencyCode } compareAtPrice { amount }
    components(first: 100) { pageInfo { hasNextPage } nodes { quantity productVariant {
      id requiresComponents requiresShipping price { amount currencyCode } compareAtPrice { amount }
      product { title handle tags }
      sellingPlanAllocations(first: 100) { pageInfo { hasNextPage } nodes {
        sellingPlan { id name } priceAdjustments { price { amount currencyCode } }
      } }
    } } }
  } } }`;
  async function load(id, fresh = false) {
    const key = String(id).split('/').pop();
    const cached = cache.get(key);
    if (!fresh && cached && Date.now() - cached.time < 15000) return cached.promise;
    const promise = fetch(`https://${KROWN.settings.store_domain}/api/2026-07/graphql.json`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': KROWN.settings.storefront_api_key },
      body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${key}` } })
    }).then(async response => {
      const body = await response.json();
      if (!response.ok || body.errors?.length || !body.data?.node) throw new Error(message('data'));
      return body.data.node;
    }).catch(error => { cache.delete(key); throw error; });
    cache.set(key, { time: Date.now(), promise });
    return promise;
  }
  function quote(variant, planId) {
    if (!variant.requiresComponents && !variant.components?.nodes?.length) return null;
    const fail = code => { throw new Error(message(code)); };
    const sale = v => Number(v.compareAtPrice?.amount || 0) > Number(v.price?.amount || 0);
    if (Number(variant.price?.amount || 0) <= 0) fail('plan');
    if (sale(variant)) fail('sale');
    if (variant.components?.pageInfo?.hasNextPage || !variant.components?.nodes?.length) fail('data');
    const items = new Map(); let price = 0, regular = 0; let currency = variant.price.currencyCode;
    for (const row of variant.components.nodes) {
      const v = row.productVariant;
      // FeeBee recalculates the deposit for the physical units added to the cart.
      if (v?.requiresShipping === false && v.product?.tags?.some(tag => tag.toLowerCase() === 'feebee fee')) continue;
      if (!v || v.requiresComponents || !Number.isInteger(row.quantity) || row.quantity <= 0) fail('data');
      if (sale(v)) fail('sale');
      const allocation = v.sellingPlanAllocations?.nodes?.find(a => a.sellingPlan.id.split('/').pop() === String(planId));
      if (!allocation || v.sellingPlanAllocations.pageInfo?.hasNextPage) fail('plan');
      const firstPrice = allocation.priceAdjustments?.[0]?.price;
      if (!firstPrice || firstPrice.currencyCode !== currency || v.price.currencyCode !== currency) fail('data');
      const id = v.id.split('/').pop();
      const existing = items.get(id);
      if (existing) existing.quantity += row.quantity;
      else items.set(id, { id, quantity: row.quantity, selling_plan: String(planId) });
      price += Math.round(Number(firstPrice.amount) * 100) * row.quantity;
      regular += Math.round(Number(v.price.amount) * 100) * row.quantity;
    }
    if (!items.size || !Number.isFinite(price) || price <= 0) fail('plan');
    return { items: [...items.values()], price, regular, currency };
  }
  async function prepare(id, planId, fresh = false) { return quote(await load(id, fresh), planId); }
  const componentPages = new Map();
  async function componentPanel(component) {
    const handle = component.product.handle;
    if (!componentPages.has(handle)) {
      const promise = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}`, { headers: { Accept: 'text/html' } })
        .then(async response => { if (!response.ok) throw new Error(message('data')); return new DOMParser().parseFromString(await response.text(), 'text/html'); })
        .catch(error => { componentPages.delete(handle); throw error; });
      componentPages.set(handle, promise);
      setTimeout(() => componentPages.delete(handle), 15000);
    }
    const html = await componentPages.get(handle);
    // The app's rendered panel supplies only managed, unpaused, eligible plans and its policy copy.
    const panel = html.querySelector(`shop-sante-subscription-options fieldset[data-variant-id="${component.id.split('/').pop()}"]`);
    if (panel) {
      const widget = panel.closest('shop-sante-subscription-options');
      panel.dataset.policyVersion = widget.dataset.policyVersion || '';
      panel.dataset.policyLocale = widget.dataset.policyLocale || '';
      panel.dataset.policyUrl = widget.dataset.policyUrl || '';
    }
    return panel;
  }
  async function cartQuote(id, planId) {
    const variant = await load(id, true);
    const result = quote(variant, planId);
    if (!result) return null;
    const component = variant.components.nodes.find(row => row.productVariant.requiresShipping !== false)?.productVariant;
    const panel = component && await componentPanel(component);
    if (!panel?.querySelector(`input[data-purchase-option="subscription"][value="${planId}"]`)) throw new Error(message('plan'));
    return { ...result, policy: panel.dataset };
  }
  async function convertCartLine({ id, key, quantity, planId }) {
    const release = await window.ShopSanteInventory.lock();
    try {
      const result = await cartQuote(id, planId);
      if (!result || result.items.length !== 1) throw new Error(message('plan'));
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error(message('data'));
      const cart = await response.json();
      const current = cart.items.find(item => item.key === key);
      if (!current || current.quantity !== quantity || current.total_discount > 0) throw new Error(message('data'));
      const item = result.items[0];
      const units = item.quantity * quantity;
      await window.ShopSanteInventory.validateAddition([{ id: item.id, quantity: units }], { ...cart, items: cart.items.filter(line => line.key !== key) });
      const properties = { ...current.properties, _ss_policy_version: result.policy.policyVersion, _ss_policy_locale: result.policy.policyLocale, _ss_policy_url: result.policy.policyUrl };
      const lineResponse = await fetch(`https://${KROWN.settings.store_domain}/api/2026-07/graphql.json`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': KROWN.settings.storefront_api_key },
        body: JSON.stringify({ query: `query BundleCartLine($id: ID!) { cart(id: $id) { lines(first: 250) { pageInfo { hasNextPage } nodes { id quantity attributes { key value } sellingPlanAllocation { sellingPlan { id } } merchandise { ... on ProductVariant { id } } } } } }`, variables: { id: `gid://shopify/Cart/${cart.token}` } })
      });
      const lineBody = await lineResponse.json();
      const lines = lineBody.data?.cart?.lines;
      if (!lineResponse.ok || lineBody.errors?.length || !lines || lines.pageInfo.hasNextPage) throw new Error(message('data'));
      const attributeKey = entries => JSON.stringify(entries.map(([key, value]) => [key, String(value)]).sort(([a], [b]) => a.localeCompare(b)));
      const currentAttributes = attributeKey(Object.entries(current.properties || {}).filter(([, value]) => value != null));
      const matches = lines.nodes.filter(line => line.merchandise?.id === `gid://shopify/ProductVariant/${id}` && line.quantity === quantity && !line.sellingPlanAllocation && attributeKey(line.attributes.map(attribute => [attribute.key, attribute.value])) === currentAttributes);
      if (matches.length !== 1) throw new Error(message('data'));
      const mutation = `mutation ConvertBundle($cart: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $cart, lines: $lines) { userErrors { field message } } }`;
      const converted = await fetch(`https://${KROWN.settings.store_domain}/api/2026-07/graphql.json`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': KROWN.settings.storefront_api_key },
        body: JSON.stringify({ query: mutation, variables: { cart: `gid://shopify/Cart/${cart.token}`, lines: [{
          id: matches[0].id, merchandiseId: `gid://shopify/ProductVariant/${item.id}`, quantity: units,
          sellingPlanId: `gid://shopify/SellingPlan/${planId}`,
          attributes: Object.entries(properties).filter(([, value]) => value != null).map(([key, value]) => ({ key, value: String(value) }))
        }] } })
      });
      const body = await converted.json();
      if (!converted.ok || body.errors?.length || body.data?.cartLinesUpdate?.userErrors?.length || !body.data?.cartLinesUpdate) throw new Error(message('data'));
      return result;
    } finally { release(); }
  }
  function isBoxFormat(variant) {
    if (!variant?.requiresComponents || variant.components?.pageInfo?.hasNextPage) return false;
    const rows = variant.components?.nodes || [];
    const physical = rows.filter(row => row.productVariant?.requiresShipping !== false);
    return physical.length > 0 && physical.every(row => Number.isInteger(row.quantity) && row.quantity > 0) && physical.reduce((sum, row) => sum + row.quantity, 0) > 1;
  }
  function bind() {
    document.querySelectorAll('[data-bundle-format]').forEach(element => {
      if (element.dataset.formatChecked) return;
      element.dataset.formatChecked = 'true';
      let ids;
      try { ids = JSON.parse(element.dataset.bundleVariants); } catch { return; }
      if (!Array.isArray(ids) || !ids.length) return;
      Promise.all(ids.map(id => load(id).catch(() => null))).then(variants => {
        element.hidden = !variants.some(isBoxFormat);
      });
    });
    document.querySelectorAll('product-page').forEach(page => {
      if (page.dataset.bundleSubscriptionBound) return;
      page.dataset.bundleSubscriptionBound = 'true';
      const variants = page.querySelector('product-variants');
      const form = page.querySelector('product-form form');
      if (!variants || !form) return;
      let revision = 0;
      const refresh = async () => {
        const id = variants.currentVariant?.id || form.querySelector('[name="id"]')?.value;
        if (!id) return;
        const serial = ++revision;
        let host = page.querySelector('[data-ss-bundle-subscriptions]');
        if (host) host.hidden = true;
        try {
          const variant = await load(id);
          if (serial !== revision || !variant.requiresComponents && !variant.components?.nodes?.length) return;
          if (!host) { host = document.createElement('div'); host.dataset.ssBundleSubscriptions = ''; host.className = 'ss-bundle-subscriptions'; form.closest('product-form').after(host); }
          host.hidden = false; host.replaceChildren(); host.dataset.variantId = String(id);
          const primary = variant.components.nodes.find(row => row.productVariant.requiresShipping !== false)?.productVariant;
          if (!primary) throw new Error(message('data'));
          const panel = await componentPanel(primary);
          if (serial !== revision) return;
          const blocked = Number(variant.compareAtPrice?.amount || 0) > Number(variant.price.amount)
            || variant.components.nodes.some(row => Number(row.productVariant.compareAtPrice?.amount || 0) > Number(row.productVariant.price?.amount || 0));
          if (!panel || blocked) {
            const notice = document.createElement('p'); notice.textContent = message(blocked ? 'sale' : 'plan'); host.append(notice); return;
          }
          const fieldset = panel.cloneNode(true);
          fieldset.classList.remove('ss-subscriptions__panel--hidden');
          fieldset.dataset.variantId = String(id);
          const money = value => new Intl.NumberFormat(french() ? 'fr-CA' : 'en-CA', { style: 'currency', currency: variant.price.currencyCode }).format(value / 100);
          const oneTime = fieldset.querySelector('input[data-purchase-option="one-time"]');
          oneTime.checked = true;
          oneTime.closest('label').querySelector('strong').textContent = money(Math.round(Number(variant.price.amount) * 100));
          let eligible = 0;
          fieldset.querySelectorAll('input[data-purchase-option]').forEach(radio => {
            radio.name = `bundle-subscription-${id}`;
            if (!radio.value) return;
            radio.checked = false;
            try {
              const result = quote(variant, radio.value);
              radio.dataset.allocationPrice = result.price;
              const label = radio.closest('label');
              const prices = label.querySelector('.ss-subscriptions__plan-price');
              prices.replaceChildren();
              const before = document.createElement('s'); before.textContent = money(result.regular);
              const total = document.createElement('strong'); total.textContent = money(result.price);
              prices.append(before, document.createTextNode(' '), total);
              const count = result.items.reduce((n, item) => n + item.quantity, 0);
              label.querySelector('.ss-subscriptions__choice-note').textContent = french() ? `${count} unités par envoi` : `${count} units per delivery`;
              eligible++;
            } catch (error) { radio.closest('label').remove(); }
          });
          if (!eligible) { host.textContent = message('plan'); return; }
          fieldset.addEventListener('change', () => {
            page.querySelector('product-form').clearProductError?.();
            page.querySelector('product-form').normalizeSubscriptionSelection?.();
            const selected = fieldset.querySelector('input:checked');
            const priceNode = page.querySelector('[data-js-product-price-original]');
            if (priceNode) priceNode.textContent = money(selected?.value ? Number(selected.dataset.allocationPrice) : Math.round(Number(variant.price.amount) * 100));
          });
          host.append(fieldset);
        } catch (error) {
          if (serial === revision && host) { host.hidden = false; host.textContent = error.message; }
        }
      };
      variants.addEventListener('VARIANT_CHANGE', refresh);
      refresh();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true }); else queueMicrotask(bind);
  document.addEventListener('shopify:section:load', bind);
  return { load, quote, prepare, cartQuote, convertCartLine, isBoxFormat, bind };
})();

if (typeof ProductVariants !== "function") {
  class ProductVariants extends HTMLElement {
    constructor() {
      super();
      this.locationProduct = {};
      this.variantData = [];
      this.storeLocationName = "";
      this.productVariantSelector = this.querySelector(
        "product-variant-selector"
      );
      (async () => {
        await this.getLocation();
        await this.getStoreName();
        this.init();
      })();
      // this.init();
    }

    async init() {
      await this.getVariantData();
      this.price = this.querySelector(`#product-price-${this.dataset.id}`);
      if (this.price) {
        this.priceOriginal = this.price.querySelector(
          "[data-js-product-price-original]"
        );
        this.priceCompare = this.price.querySelector(
          "[data-js-product-price-compare]"
        );
        this.priceUnit = this.price.querySelector(
          "[data-js-product-price-unit]"
        );
        this.priceSaving = this.price.querySelector(
          "[data-js-flash-sale-saving]"
        );
      }

      this.productForm = document.querySelector(
        `#product-form-${this.dataset.id}`
      );
      if (this.productForm) {
        this.productQty = this.productForm.querySelector(
          "[data-js-product-quantity]"
        );
        this.addToCart = this.productForm.querySelector(
          "[data-js-product-add-to-cart]"
        );
        this.addToCartText = this.productForm.querySelector(
          "[data-js-product-add-to-cart-text]"
        );
      }
      this.addEventListener("change", this.onVariantChange);
      document.addEventListener('storechanged', async () => {
        this.locationProduct = {};
        await this.getLocation();
        this.querySelectorAll('.product-variant__item input.disabled').forEach(item => item.classList.remove('disabled'));
        this.dispatchEvent(new Event('change'));
      });
      window.addEventListener("load", () => {
        document
          .querySelectorAll("[data-js-select-store] [data-js-change-store]")
          .forEach((element) => {
            element.addEventListener("click", (e) => {
              this.querySelectorAll(
                ".product-variant__item input.disabled"
              ).forEach((item) => item.classList.remove("disabled"));
              this.dispatchEvent(new Event("change"));
            });
          });
      });
      window.addEventListener("load", () => {
        document
          .querySelectorAll("[data-js-reset-store]")
          .forEach((resetButton) => {
            resetButton.addEventListener("click", (e) => {
              this.querySelectorAll(
                ".product-variant__item input.disabled"
              ).forEach((item) => item.classList.remove("disabled"));
              this.dispatchEvent(new Event("change"));
            });
          });
      });

      let initVariantChange = false;
      if (this.hasAttribute("data-variant-required")) {
        this.variantRequired = true;
        this.noVariantSelectedYet = true;
        if (document.location.search.includes("variant")) {
          const initVariant = parseInt(
            new URLSearchParams(document.location.search).get("variant")
          );
          const variantData = this.variantData;

          this.currentVariant = variantData.find((variant) => {
            if (variant.id == initVariant) {
              variant.options.map((option, index) => {
                const variantContainer = this.querySelectorAll(
                  `[data-js-product-variant]`
                )[index].querySelector(`[data-js-product-variant-container]`);
                if (
                  variantContainer.dataset.jsProductVariantContainer == "radio"
                ) {
                  variantContainer.querySelector(
                    `input[value="${option}"]`
                  ).checked = true;
                } else if (
                  variantContainer.dataset.jsProductVariantContainer == "select"
                ) {
                  variantContainer.value = option;
                }
              });
              return variant;
            }
          });

          if (this.currentVariant) {
            this.variantRequired = false;
            this.noVariantSelectedYet = false;
            initVariantChange = true;
          }
        }
      } else {
        this.variantRequired = false;
        this.noVariantSelectedYet = false;
      }
      this.updateOptions();
      this.updateMasterId();
      const autoSelectedAvailableVariant = !new URLSearchParams(window.location.search).has('variant')
        && this.selectFirstAvailableVariant();
      this.updateUnavailableVariants();
      this.checkBtnAddToCart();
      // The quantity limit must belong to the selected flavor, not the first
      // option in the DOM (which may be sold out).
      if (this.currentVariant) this.getQuantityLocation(this.currentVariant.id);

      this.productStock = document.querySelector(
        `#product-${this.dataset.id} [data-js-variant-quantity]`
      );
      this.productStockProgress = document.querySelector(
        `#product-${this.dataset.id} [data-js-variant-quantity-progress]`
      );
      if (
        this.productStock &&
        document.querySelector(
          `#product-${this.dataset.id} [data-js-variant-quantity-data]`
        )
      ) {
        const inventoryDataElement = document.querySelector(
          `#product-${this.dataset.id} [data-js-variant-quantity-data]`
        );
        try {
          this.productStockData = JSON.parse(
            inventoryDataElement.dataset.inventory || inventoryDataElement.textContent || "[]"
          );
        } catch (error) {
          this.productStockData = [];
        }
      }
      this.updateStock();

      if (initVariantChange || autoSelectedAvailableVariant) {
        await this.onVariantChange();
      }
      this.updatePickupAvailabilityEachStore();

      this.dispatchEvent(new Event("INITLOCATION"));
    }
    async getLocation() {
      try {
        const token = window.KROWN.settings.storefront_api_key;
        const domain = window.KROWN.settings.store_domain;
        const productPage = this.closest("product-page");
        if (productPage?.dataset.onlineInventoryOnly === 'true') {
          this.locationProduct = {};
          return null;
        }
        const productId = productPage?.dataset.id || this.dataset.productId;

        if (!productId) {
          throw new Error("Product ID is missing from the product page");
        }

        const data = await window.ShopSanteInventory.load({
          productId,
          domain,
          token,
        });

        if (!data) return null;
        this.locationProduct = data;

        return this.locationProduct;
      } catch (error) {
        this.locationProduct = {};
        return null;
      }
    }
    resolveSelectedStoreName() {
      return window.ShopSanteInventory.storeName(this.locationProduct?.data?.product);
    }

    async getStoreName() {
      const storeName = this.resolveSelectedStoreName();
      this.storeLocationName = storeName;
      return storeName;
    }

    getInventoryPolicies() {
      const policies = this.querySelector("script[data-js-variant-inventory-policy]");
      if (!policies) return [];

      try {
        return JSON.parse(policies.textContent || "[]");
      } catch (error) {
        return [];
      }
    }

    findLocationVariant(localVariant, variants = this.locationProduct?.data?.product?.variants?.nodes || []) {
      if (this.closest('product-page')?.dataset.onlineInventoryOnly === 'true') {
        return { ...localVariant, liquidOnlineInventory: true };
      }
      return variants.find(item => String(item.id).split('/').pop() === String(localVariant?.id)) || null;
    }

    getLocationState(item, storeName) {
      if (item?.liquidOnlineInventory === true) {
        const quantity = item.inventory_policy === 'continue' && item.available
          ? Infinity : Math.max(0, Math.floor(Number(item.inventory_quantity) || 0));
        return { quantity, available: item.available === true && quantity > 0, isBundle: false, unlimited: quantity === Infinity };
      }
      return window.ShopSanteInventory.state(item, storeName);
    }

    async getVariantData() {
      const onlineOnly = this.closest('product-page')?.dataset.onlineInventoryOnly === 'true';
      if (!onlineOnly && !this.locationProduct?.data?.product) await this.getLocation();
      const variants = JSON.parse(this.querySelector('[data-js-variants-data]').textContent);
      const storeName = onlineOnly ? 'Shop Santé Inc.' : await this.getStoreName();
      this.storeLocationName = storeName;
      let liquidInventory = [];
      if (onlineOnly) {
        liquidInventory = this.getInventoryPolicies();
      }
      this.variantData = variants.map(variant => {
        if (onlineOnly) {
          const inventory = liquidInventory.find(item => String(item.id) === String(variant.id));
          const quantity = inventory?.inventory === 'continue' && !inventory?.unavailable
            ? Infinity : Math.max(0, Math.floor(Number(inventory?.quantity) || 0));
          const restricted = this.querySelector(`[data-online-hidden="true"][data-variant-id="${variant.id}"]`);
          return {
            ...variant,
            inventory_policy: inventory?.inventory || 'deny',
            inventory_quantity: quantity,
            available: variant.available === true && quantity > 0 && !restricted,
          };
        }
        const state = this.getLocationState(this.findLocationVariant(variant), storeName);
        let restricted = this.querySelector(`[data-online-hidden="true"][data-variant-id="${variant.id}"]`);
        if (state.isBundle && restricted) { restricted.removeAttribute('data-online-hidden'); restricted.removeAttribute('disabled'); restricted.removeAttribute('aria-disabled'); restricted = null; }
        return { ...variant, available: state.available && !restricted };
      });
    }

    checkBtnAddToCart() {
      if (this.productForm) {
        if (!this.currentVariant || !this.currentVariant.available) {
          if (this.productQty) this.productQty.style.display = "none";
          this.addToCart?.classList.add("disabled");
		  if (this.addToCart) this.addToCart.disabled = true;
		  this.addToCart?.setAttribute("aria-disabled", "true");
          this.productForm.classList.add("disabled-cart");
          if (this.addToCartText) {
            this.addToCartText.textContent = KROWN.settings.locales.products_sold_out_variant;
          }
        } else {
          if (this.productQty) this.productQty.style.display = "";
          this.addToCart?.classList.remove("disabled");
		  if (this.addToCart) this.addToCart.disabled = false;
		  this.addToCart?.setAttribute("aria-disabled", "false");
          this.productForm.classList.remove("disabled-cart");
          if (this.addToCartText) {
            this.addToCartText.textContent = this.hasAttribute(
              "data-show-bundle-wording"
            )
              ? KROWN.settings.locales.products_add_to_bundle_button
              : KROWN.settings.locales.products_add_to_cart_button;
          }
        }
        if (!this.currentVariant) {
          this.productForm.classList.add("unavailable-variant");
          if (this.addToCartText) {
            this.addToCartText.textContent =
              this.variantRequired && this.noVariantSelectedYet
                ? KROWN.settings.locales.products_variant_required
                : KROWN.settings.locales.products_unavailable_variant;
          }
        } else {
          this.productForm.classList.remove("unavailable-variant");
        }
      }
    }
    async onVariantChange() {
      await this.getStoreName();
      await this.getVariantData();
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantInput();
      if (this.currentVariant) {
        this.getQuantityLocation(this.currentVariant.id);
      }
      if (this.price) {
        this.updatePrice();
      }
      this.updatePickupAvailabilityEachStore();
      this.updateStock();
      this.updateUnavailableVariants();
      this.updatePickupAvailability();
      if (!this.hasAttribute("data-no-history")) {
        this.updateURL();
      }
      this.updatePhotoMetafields();
      this.updateDetails();
      this.checkBtnAddToCart();
      this.dispatchEvent(new Event("VARIANT_CHANGE"));
    }
    updatePhotoMetafields() {
      if (!this.currentVariant) return;

      const scope = this.closest("product-page, #product-quick-view") || document;
      const metafieldData = scope.querySelector("[data-js-variant-metafield]");
      const image = scope.querySelector("[data-js-nutrition_facts]");
      if (!metafieldData || !image) return;

      let arrayVariantsMetafields;
      try {
        arrayVariantsMetafields = JSON.parse(metafieldData.textContent || "[]");
      } catch (error) {
        return;
      }

      const imgDefault = image.textContent.trim();
      const variantMetafieldImage = arrayVariantsMetafields.find(
        (item) => Number(item.id) === Number(this.currentVariant.id)
      );
      if (!variantMetafieldImage) return;

      const imgInfo = scope.querySelector("toggle-tab img");
      if (imgInfo != null) {
        if (variantMetafieldImage.image != "undefined") {
          imgInfo.srcset = variantMetafieldImage.image;
          imgInfo.src = variantMetafieldImage.image;
        } else {
          imgInfo.srcset = imgDefault;
          imgInfo.src = imgDefault;
        }
        imgInfo.closest("figure")?.querySelector(".img-magnifier-glass")?.remove();
        if (typeof window.magnify === "function") window.magnify("img-magnifier", 3);
      }
    }
    updateOptions() {
      this.options = [];

      this.querySelectorAll("[data-js-product-variant-container]").forEach(
        (elm) => {
          if (elm.dataset.jsProductVariantContainer == "radio") {
            elm.querySelectorAll(".product-variant__input").forEach((el) => {
              if (el.checked) {
                this.options.push(el.value);
              }
            });
          } else {
            if (!(this.variantRequired && elm.selectedIndex == 0)) {
              this.options.push(elm.value);
            }
          }
        }
      );

      if (
        this.variantRequired &&
        this.noVariantSelectedYet &&
        this.options.length >= parseInt(this.dataset.variants)
      ) {
        this.noVariantSelectedYet = false;
      }
    }
    async updateMasterId() {

      this.currentVariant = this.variantData.find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    selectFirstAvailableVariant() {
      if (
        (this.variantRequired && this.noVariantSelectedYet) ||
        this.currentVariant?.available
      ) {
        return false;
      }

      const firstAvailableVariant = this.variantData.find(
        (variant) => variant.available
      );

      if (!firstAvailableVariant) {
        return false;
      }

      const optionContainers = this.querySelectorAll(
        "[data-js-product-variant-container]"
      );

      firstAvailableVariant.options.forEach((option, index) => {
        const container = optionContainers[index];
        if (!container) return;

        if (container.dataset.jsProductVariantContainer === "radio") {
          const input = Array.from(
            container.querySelectorAll(".product-variant__input")
          ).find((item) => item.value === option);

          if (input) input.checked = true;
          return;
        }

        container.value = option;

        const selector = container.closest("product-variant-selector");
        if (!selector) return;

        const selectedItem = Array.from(
          selector.querySelectorAll(".product-variant-value")
        ).find((item) => item.dataset.variantValue === option);

        selector
          .querySelectorAll(".product-variant-value")
          .forEach((item) => item.classList.remove("selected"));

        if (selectedItem) {
          selectedItem.classList.add("selected");
          const title = selector.querySelector("[data-variant-title]");
          const label = selector.querySelector("[data-variant-label]");

          if (title) {
            title.textContent = selectedItem.dataset.variantTitle || option;
          }
          if (label) {
            label.textContent = selectedItem.dataset.variantLabel || "";
          }
        }
      });

      this.options = [...firstAvailableVariant.options];
      this.currentVariant = firstAvailableVariant;

      return true;
    }

    updatePrice() {
      if (!this.currentVariant) {
        if (!(this.variantRequired && this.noVariantSelectedYet)) {
          if (this.priceOriginal) this.priceOriginal.innerHTML = "";
          if (this.priceCompare) this.priceCompare.innerHTML = "";
          if (this.priceUnit) this.priceUnit.innerHTML = "";
          if (this.priceSaving) {
            this.priceSaving.textContent = "";
            this.priceSaving.hidden = true;
          }
        }
      } else {
        if (this.priceOriginal) {
          this.priceOriginal.innerHTML = this._formatMoney(
            this.currentVariant.price,
            KROWN.settings.shop_money_format
          );
        }
        if (this.currentVariant.compare_at_price > this.currentVariant.price) {
          if (this.priceCompare) {
            this.priceCompare.innerHTML = this._formatMoney(
              this.currentVariant.compare_at_price,
              KROWN.settings.shop_money_format
            );
          }
          if (this.priceSaving) {
            this.priceSaving.textContent = `-${Math.round(
              Math.abs(
                (this.currentVariant.price * 100) /
                  this.currentVariant.compare_at_price -
                  100
              )
            )}%`;
            this.priceSaving.hidden = false;
          }
        } else {
          if (this.priceCompare) this.priceCompare.innerHTML = "";
          if (this.priceSaving) {
            this.priceSaving.textContent = "";
            this.priceSaving.hidden = true;
          }
        }

        if (this.currentVariant.unit_price_measurement) {
          if (this.priceUnit) this.priceUnit.innerHTML = `
						${this._formatMoney(
              this.currentVariant.unit_price,
              KROWN.settings.shop_money_format
            )} /
						${
              this.currentVariant.unit_price_measurement.reference_value != 1
                ? this.currentVariant.unit_price_measurement.reference_value +
                  " "
                : ""
            }
						${this.currentVariant.unit_price_measurement.reference_unit}
					`;
        } else {
          if (this.priceUnit) this.priceUnit.innerHTML = "";
        }
      }
    }

    updateStock() {
      if (!this.currentVariant) {
        if (this.productStock) {
          this.productStock.innerHTML = "";
        }
      } else {
        if (this.productStock && this.productStockData) {
          let currentVariant = false;
          for (const variant of this.productStockData) {
            if (variant.id == this.currentVariant.id) {
              currentVariant = variant;
              break;
            }
          }
          const state = this.getLocationState(this.findLocationVariant(this.currentVariant), this.storeLocationName);
          currentVariant = { ...currentVariant, quantity: state.quantity, inventory: state.unlimited ? 'continue' : 'deny' };
          this.productStock.innerHTML = "";

          if (currentVariant) {
            if (currentVariant.inventory == "continue") {
              this.productStock.textContent = document.documentElement.lang?.toLowerCase().startsWith('fr')
                ? 'Plus de 200 en stock' : 'Over 200 in stock';
            } else if (currentVariant.quantity <= 0) {
              if (currentVariant.inventory == "deny") {
                this.productStock.innerHTML =
                  KROWN.settings.locales.products_no_products;
              }
            } else if (currentVariant.quantity == "1") {
              this.productStock.innerHTML =
                KROWN.settings.locales.products_one_product;
            } else if (
              currentVariant.quantity <=
              parseInt(this.productStock.dataset.lowStock)
            ) {
              this.productStock.innerHTML =
                KROWN.settings.locales.products_few_products.replace(
                  "{{ count }}",
                  currentVariant.quantity
                );
            } else if (currentVariant.unavailable) {
              this.productStock.innerHTML =
                KROWN.settings.locales.products_no_products;
            } else if (
              currentVariant.quantity >
                parseInt(this.productStock.dataset.lowStock) &&
              this.productStock.dataset.type == "always"
            ) {
              this.productStock.innerHTML =
                KROWN.settings.locales.products_few_products.replace(
                  "{{ count }}",
                  currentVariant.quantity
                );
            } else if (!currentVariant.quantity) {
              this.productStock.innerHTML =
                KROWN.settings.locales.products_enough_products;
            }

            if (this.productStockProgress) {
              let progressQty = 0;
              if (
                (currentVariant.quantity <= 0 &&
                  currentVariant.inventory == "continue") ||
                typeof currentVariant.quantity === "undefined"
              ) {
                progressQty = parseInt(this.productStock.dataset.highStock);
              } else if (currentVariant.quantity > 0) {
                progressQty = currentVariant.quantity;
              }
              if (
                progressQty >= parseInt(this.productStock.dataset.highStock)
              ) {
                this.productStockProgress.style.width = `100%`;
              } else {
                this.productStockProgress.style.width = `${
                  (100 * progressQty) /
                  parseInt(this.productStock.dataset.highStock)
                }%`;
              }
            }
          }
        }
      }
    }

    updateDetails() {
      document
        .querySelectorAll(`#product-${this.dataset.id} [data-js-product-sku]`)
        .forEach((elm) => {
          if (this.currentVariant && this.currentVariant.sku) {
            elm.innerHTML =
              KROWN.settings.locales.product_sku + this.currentVariant.sku;
          } else {
            elm.innerHTML = "";
          }
        });

      document
        .querySelectorAll(
          `#product-${this.dataset.id} [data-js-product-barcode]`
        )
        .forEach((elm) => {
          if (this.currentVariant && this.currentVariant.barcode) {
            elm.innerHTML =
              KROWN.settings.locales.product_barcode +
              this.currentVariant.barcode;
          } else {
            elm.innerHTML = "";
          }
        });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector("pickup-availability");
      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute("available");
        pickUpAvailability.innerHTML = "";
      }
    }

    updatePickupAvailabilityEachStore() {
      if (!this.locationProduct?.data?.product?.variants?.nodes) return;
      if (!this.currentVariant) return;

      const allStores = document.querySelectorAll(
        ".store-selector-item[data-title]"
      );

      const variantsLocation = this.findLocationVariant(
        this.currentVariant,
        this.locationProduct.data.product.variants.nodes
      );

      if (!variantsLocation) return;

      allStores.forEach((store) => {
        const itemName = store.getAttribute("data-title");
        const itemAvailable = store.querySelector(
          ".store-selector-item__available"
        );

        if (!itemAvailable) return;

        const state = this.getLocationState(variantsLocation, itemName);

        itemAvailable.classList.toggle("alert--success", state.available);
        itemAvailable.classList.toggle("alert--error", !state.available);
      });
    }

    updateUnavailableVariants() {
      if (this.dataset.hideVariants != "false") {
        let options1 = {},
          options2 = {},
          options3 = {},
          option1Selected = null,
          option2Selected = null;

        if (this.dataset.variants > 0) {
          option1Selected = this._getSelectedOption(0);
          this._resetDisabledOption(0);
        }
        if (this.dataset.variants > 1) {
          option2Selected = this._getSelectedOption(1);
          this._resetDisabledOption(1);
        }

        this.variantData.forEach((el) => {
          if (this.dataset.variants > 0) {
            if (!options1[el.option1]) {
              options1[el.option1] = [];
            }
            options1[el.option1].push(String(el.available));
          }
          if (this.dataset.variants > 1) {
            if (!options2[el.option2]) {
              options2[el.option2] = [];
            }
            options2[el.option2].push(String(el.available));
          }
          if (this.dataset.variants == 2) {
            if (el.option1 == option1Selected && !el.available) {
              this._setDisabledOption(1, el.option2);
            }
          }
          if (this.dataset.variants > 2) {
            if (!options3[el.option3]) {
              options3[el.option3] = [];
            }
            options3[el.option3].push(String(el.available));
            if (
              el.option2 == option2Selected &&
              el.option1 == option1Selected &&
              !el.available
            ) {
              this._setDisabledOption(2, el.option3);
            }
          }
        });

        if (this.dataset.variants > 0) {
          Object.keys(options1).forEach((key) => {
            if (!options1[key].includes("true")) {
              this._setDisabledOption(0, key);
            }
          });
        }
        if (this.dataset.variants > 1) {
          Object.keys(options2).forEach((key) => {
            if (!options2[key].includes("true")) {
              this._setDisabledOption(1, key);
            }
          });
        }
        if (this.dataset.variants > 2) {
          Object.keys(options3).forEach((key) => {
            if (!options3[key].includes("true")) {
              this._setDisabledOption(2, key);
            }
          });
          Object.keys(options2).forEach((key) => {
            if (!options2[key].includes("true")) {
              if (option2Selected == key) {
                if (this.dataset.hideVariants == "disable") {
                  this.querySelectorAll("[data-js-product-variant]")[2]
                    .querySelector(".product-variant-value")
                    .setAttribute("disabled", "disabled");
                } else {
                  this.querySelectorAll("[data-js-product-variant]")[2]
                    .querySelector(".product-variant-value")
                    .classList.add("disabled");
                }
              }
            }
          });
        }
      }
    }

    _getSelectedOption(i) {
      let selectedOption = null;
      this.querySelectorAll("[data-js-product-variant]")
        [i].querySelectorAll("[data-js-product-variant-container]")
        .forEach((elm) => {
          if (elm.dataset.jsProductVariantContainer == "radio") {
            elm.querySelectorAll(".product-variant__input").forEach((el) => {
              if (el.checked) {
                selectedOption = el.value;
              }
            });
          } else {
            selectedOption = elm.value;
          }
        });
      return selectedOption;
    }

    _resetDisabledOption(i) {
      this.querySelectorAll("[data-js-product-variant]")
        [i].querySelectorAll(".product-variant-value")
        .forEach((elm) => {
          if (elm.dataset.onlineHidden === 'true') return;
          elm.classList.remove("disabled");
          elm.removeAttribute("aria-disabled");
          elm.classList.remove("hide-variant");
          elm.hidden = false;
          if (!elm.hasAttribute("default")) {
            elm.removeAttribute("disabled");
          }
          if (elm.parentNode) {
            elm.parentNode.classList.remove("hide-variant");
            elm.parentNode.hidden = false;
          }
        });

      this.querySelectorAll("[data-js-product-variant]")
        [i].querySelectorAll("option")
        .forEach((option) => {
          option.disabled = false;
          option.hidden = false;
        });
    }

    _setDisabledOption(i, option) {
      const variantGroup = this.querySelectorAll("[data-js-product-variant]")[i];
      if (!variantGroup) return;

      const escapedOption = option.replace('"', "&x22;");
      let variant = variantGroup.querySelector(
        `.product-variant-value[value="${escapedOption}"]`
      );

      const nativeOption = Array.from(variantGroup.querySelectorAll("option"))
        .find((item) => item.value === option);

      if (nativeOption) {
        nativeOption.disabled = true;
        nativeOption.hidden = false;
      }

      if (variant) {
        variant.setAttribute("disabled", "disabled");
        variant.setAttribute("aria-disabled", "true");
        variant.classList.add("disabled");
        variant.classList.remove("hide-variant");
        variant.hidden = false;

        if (variant.parentNode) {
          variant.parentNode.classList.remove("hide-variant");
          variant.parentNode.hidden = false;
        }
      }
    }

    updateURL() {
      if (!this.currentVariant) return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }
    getQuantityLocation(activeId) {
      const variants = this.locationProduct?.data?.product?.variants?.nodes;
      if (!variants) return;

      const localVariant = this.variantData.find((variant) => Number(variant.id) === Number(activeId));
      const activeItem = this.findLocationVariant(localVariant, variants);


      const state = this.getLocationState(activeItem, this.storeLocationName);
      const canOversellOnline = false;
      const limit = canOversellOnline ? 9999 : state.quantity;
      const productForm = this.productForm || this.closest("product-page")?.querySelector('form[action*="/cart/add"]');

      productForm?.setAttribute("data-limit-location", String(limit));
      this.closest("product-page")?.querySelector('[data-js-product-form]')
        ?.setAttribute("data-limit-location", String(limit));

      const quantityPlus = productForm?.querySelector(".qty-plus, .product-quantity__plus");
      const currentQuantityInput = productForm?.querySelector(".qty-selector, .product-quantity__selector");

      if (currentQuantityInput) currentQuantityInput.max = String(Math.max(0, limit));
      if (quantityPlus && currentQuantityInput) {
        const currentValue = Math.max(1, Number(currentQuantityInput.value || 1));
        if (!canOversellOnline && (limit <= 0 || currentValue >= limit)) {
          quantityPlus.classList.add("disabled");
          if (limit > 0 && currentValue > limit) currentQuantityInput.value = String(limit);
        } else {
          quantityPlus.classList.remove("disabled");
        }
      }

      return state.quantity;
    }
    updateVariantInput() {
      if (!this.currentVariant) return;
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.id}, #product-form-installment`
      );
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    _formatMoney(cents, format) {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }

      let value = "";
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ",";
        decimal = decimal || ".";

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split(".");
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          "$1" + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : "";

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }
  }

  if (typeof customElements.get("product-variants") == "undefined") {
    customElements.define("product-variants", ProductVariants);
  }
}

/* ---
	Product Form
--- */

if (typeof ProductForm !== "function") {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      if (this.hasAttribute("data-ajax-cart")) {
        this.form = this.querySelector("form");
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.form.addEventListener("change", () => this.clearProductError());
        this.ADD_TO_CART = new Event("add-to-cart");
      }
    }

    async onSubmitHandler(e) {
      e.preventDefault();

      const submitButton = this.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled || submitButton.getAttribute("aria-busy") === "true") return;
      this.clearProductError();
      this.normalizeSubscriptionSelection();
      submitButton.classList.add("working");
      submitButton.setAttribute("aria-busy", "true");

      const showAlert = (message) => {
        this.querySelector(".alert--error")?.remove();
        const alert = document.createElement("span");
        alert.style.margin = "0 auto";
        alert.className = "alert alert--error";
        alert.setAttribute("role", "alert");
        alert.textContent = message || KROWN.settings.locales.cart_general_error;
        this.append(alert);
      };

      const releaseInventory = await window.ShopSanteInventory.lock();
      try {
        const formData = new FormData(this.form);
        const ids = formData.getAll('id').concat(formData.getAll('id[]'));
        const quantity = Number(formData.get('quantity') || 1);
        if (!ids.length) throw new Error(KROWN.settings.locales.cart_general_error);
        const sellingPlan = String(formData.get('selling_plan') || '');
        const bundleQuote = sellingPlan && ids.length === 1
          ? await window.ShopSanteBundleSubscriptions.prepare(ids[0], sellingPlan, true) : null;
        const selectedBundlePlan = this.closest('product-page')?.querySelector('[data-ss-bundle-subscriptions] input:checked');
        if (bundleQuote && Number(selectedBundlePlan?.dataset.allocationPrice) !== bundleQuote.price) {
          throw new Error(document.documentElement.lang.startsWith('fr') ? 'Le prix a changé. Actualisez la page pour confirmer le nouveau total.' : 'The price changed. Refresh the page to confirm the new total.');
        }
        const properties = Object.fromEntries([...formData.entries()].filter(([key]) => /^properties\[.*\]$/.test(key)).map(([key, value]) => [key.slice(11, -1), value]));
        if (bundleQuote) {
          const policy = selectedBundlePlan?.closest('fieldset')?.dataset;
          if (!policy?.policyVersion) throw new Error('Subscription policy unavailable');
          properties._ss_policy_version = policy.policyVersion;
          properties._ss_policy_locale = policy.policyLocale;
          if (policy.policyUrl) properties._ss_policy_url = policy.policyUrl;
        }
        const resolvedItems = bundleQuote?.items.map(item => ({ ...item, quantity: item.quantity * quantity, properties }));
        if (!this.closest('[data-online-inventory-only="true"]')) {
          await window.ShopSanteInventory.validateAddition(resolvedItems || ids.map(id => ({ id, quantity })));
        }
        const addResponse = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          body: resolvedItems ? JSON.stringify({ items: resolvedItems }) : this._serializeForm(this.form),
          headers: {
            "Content-Type": resolvedItems ? "application/json" : "application/x-www-form-urlencoded",
            "X-Requested-With": "XMLHttpRequest",
          },
          method: "POST",
        });
        const addResult = await addResponse.json();

        if (!addResponse.ok || addResult.status === 422) {
          showAlert(addResult.description || addResult.message);
          return;
        }

        const isCosties = Boolean(
          window.ShopSanteCustomer?.isLoggedIn &&
          window.ShopSanteCustomer?.isCosties
        );

        if (isCosties) {
          const desiredBlockValue = window.COSTIES_CUSTOMER_ONLINE_ALLOWED
            ? "false"
            : "true";
          const cartResponse = await fetch(`${KROWN.settings.routes.cart_url}.js`, {
            headers: { "Accept": "application/json" },
          });
          const cart = await cartResponse.json();

          if (cart.attributes?.costies_block_online !== desiredBlockValue) {
            await fetch(`${KROWN.settings.routes.cart_url.replace('/cart', '/cart/update')}.js`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: JSON.stringify({
                attributes: {
                  ...(cart.attributes || {}),
                  costies_block_online: desiredBlockValue,
                },
              }),
            });
          }
        }

        if (typeof window.ShopSanteRefreshCart === "function") {
          await window.ShopSanteRefreshCart();
        } else {
          const sectionUrl = new URL(window.location.pathname, window.location.origin);
          sectionUrl.searchParams.set("section_id", "helper-cart");
          const sectionResponse = await fetch(sectionUrl.toString(), {
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          const text = await sectionResponse.text();
          const sectionDocument = new DOMParser().parseFromString(text, "text/html");
          const newCartForm = sectionDocument.getElementById("AjaxCartForm");
          const newCartSubtotal = sectionDocument.getElementById("AjaxCartSubtotal");
          const cartItems = document.getElementById("AjaxCartForm");
          const cartSubtotal = document.getElementById("AjaxCartSubtotal");

          if (!newCartForm || !newCartSubtotal || !cartItems || !cartSubtotal) {
            throw new Error("Unable to refresh cart");
          }

          cartItems.innerHTML = newCartForm.innerHTML;
          cartItems.ajaxifyCartItems?.();
          cartSubtotal.innerHTML = newCartSubtotal.innerHTML;

          document.querySelectorAll("[data-header-cart-count]").forEach((elm) => {
            const count = cartItems.querySelector("[data-cart-count]");
            if (count) elm.textContent = count.textContent;
          });
          document.querySelectorAll("[data-header-cart-total]").forEach((elm) => {
            const total = cartItems.querySelector("[data-cart-total]");
            if (total) elm.textContent = total.textContent;
          });
          if (typeof window.InitSubmit === "function") window.InitSubmit();
        }

        this.dispatchEvent(this.ADD_TO_CART);
      } catch (error) {
        console.error("Unable to add product to cart", error);
        showAlert(error.message || KROWN.settings.locales.cart_general_error);
      } finally {
        releaseInventory();
        submitButton.classList.remove("working");
        submitButton.removeAttribute("aria-busy");
      }
    }

    clearProductError() {
      this.querySelector(".alert--error")?.remove();
    }

    normalizeSubscriptionSelection() {
      const scope = this.closest(".shopify-section") || this.closest("product-page");
      const widget = scope?.querySelector("shop-sante-subscription-options:not([hidden])");
      const bundle = scope?.querySelector('[data-ss-bundle-subscriptions]:not([hidden])');
      const selected = bundle
        ? (bundle.querySelector('input[data-purchase-option]:checked') || { value: '' })
        : widget?.querySelector('[data-variant-id]:not(.ss-subscriptions__panel--hidden) input[data-purchase-option]:checked');

      if (!selected) return;

      const selectedPlan = selected.value || "";
      const inputs = Array.from(this.form.querySelectorAll('input[name="selling_plan"]'));

      if (!inputs.length) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "selling_plan";
        this.form.append(input);
        inputs.push(input);
      }

      inputs.forEach((input, index) => {
        input.value = index === 0 ? selectedPlan : "";
        input.disabled = !selectedPlan || index > 0;
      });
    }

    _serializeForm(form) {
      let arr = [];
      Array.prototype.slice.call(form.elements).forEach(function (field) {
        if (
          !field.name ||
          field.disabled ||
          ["file", "reset", "submit", "button"].indexOf(field.type) > -1
        )
          return;
        if (field.type === "select-multiple") {
          Array.prototype.slice.call(field.options).forEach(function (option) {
            if (!option.selected) return;
            arr.push(
              encodeURIComponent(field.name) +
                "=" +
                encodeURIComponent(option.value)
            );
          });
          return;
        }
        if (["checkbox", "radio"].indexOf(field.type) > -1 && !field.checked)
          return;
        arr.push(
          encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value)
        );
      });
      return arr.join("&");
    }
  }

  if (typeof customElements.get("product-form") == "undefined") {
    customElements.define("product-form", ProductForm);
  }
}

/* ---
	Product Recommendations
--- */

if (typeof ProductRecommendations !== "function") {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();

      const event = new Event("product-recommendations-loaded");

      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);

        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((text) => {
            const innerHTML = new DOMParser()
              .parseFromString(text, "text/html")
              .querySelector("product-recommendations");

            if (
              innerHTML &&
              innerHTML.querySelectorAll("[data-js-product-item]").length > 0
            ) {
              this.innerHTML = innerHTML.innerHTML;
              this.querySelectorAll("form").forEach((elm) => {
                if (elm.querySelector("template")) {
                  elm.append(
                    elm.querySelector("template").content.cloneNode(true)
                  );
                }
              });
              this.dispatchEvent(event);
            }
          })
          .catch((e) => {
            console.error(e);
          });
      };

      new IntersectionObserver(handleIntersection.bind(this), {
        rootMargin: `0px 0px 400px 0px`,
      }).observe(this);
    }
  }

  if (typeof customElements.get("product-recommendations") == "undefined") {
    customElements.define("product-recommendations", ProductRecommendations);
  }
}

if (typeof ProductVariantSelector !== "function") {
  class ProductVariantSelector extends HTMLElement {
    constructor() {
      super();
      if (this.querySelector("details")) {
        this.init();
      }
    }
    init() {
      const select = this.querySelector("select");
      const details = this.querySelector("details");
      details.addEventListener("click", (event) => {
        const item = event.target.closest("li");
        if (item) {
          if (item.hidden || item.classList.contains("disabled") || item.classList.contains("hide-variant") || item.hasAttribute("disabled")) {
            event.preventDefault();
            return;
          }
          const value = item.getAttribute("data-variant-value");
          const label = item.getAttribute("data-variant-label");
          const title = item.getAttribute("data-variant-title");
          this.querySelector("summary [data-variant-title]").textContent =
            title;
          this.querySelector("summary [data-variant-label]").textContent =
            label;

          const items = details.querySelectorAll("ul > li");

          items.forEach((item) => {
            item.classList.remove("selected");
          });
          item.classList.add("selected");

          if (select) {
            const event = new Event("change", {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            select.value = value;
            select.dispatchEvent(event);
          }
          details.open = false;
          this.dispatchEvent(
            new CustomEvent("product-variant-selector:changed", {
              bubbles: true,
              detail: { value },
            })
          );
        }
      });
      document.addEventListener("click", (event) => {
        if (!details.contains(event.target) && details.open) {
          details.open = false;
        }
      });
    }
  }

  if (typeof customElements.get("product-variant-selector") == "undefined") {
    customElements.define("product-variant-selector", ProductVariantSelector);
  }
}
